import { FC, useState, useEffect } from 'react';
import { getFile } from 'src/services/schoolRequest';
import { useLocation } from 'react-router-dom';
import { ErrorNotice, Loader } from 'src/generic/component';

import { IPhone, ISummary } from 'src/types';
import { useAuth } from 'src/contexts';
import { TDISchool } from './types';
import { findDiOrg } from './apis/findDi';
import { IData } from 'src/types/org/common/dataSchema';
import { IDIRequest } from 'src/types/org/di/diRequestSchema';
import { DIUpdateRequestForm } from './components/DiUpateRequestForm';

const READ_ONLY_STATUSES = ['processing', 'opsApproved', 'completed'];
const isReadOnly = (summary: ISummary) => {
  const { processStatus } = summary || {};

  const cannotResubmit = (processStatus === 'rejected');
  return (READ_ONLY_STATUSES.includes(processStatus) || cannotResubmit);
};

// Retrieve the DI form using the categoryId and userId with a custom hook here...
const useFile = (file: string) => {
  const [data, setData] = useState<any | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [errorLoading, setError] = useState<string>('');

  const retrieve = async (file: string) => {
    setLoading(true);
    try {
      const request = await getFile(file);
      request && setData(request);
    } catch (error: any) {
      setError(error?.message || error);
    }
    setLoading(false);
  };

  useEffect(() => {
    retrieve(file);
  }, [file]);

  return { data, setData, isLoading, errorLoading };
};

const mapDI = async (dischool: TDISchool): Promise<IData> => {
  const record = await findDiOrg(dischool.orgId);

  const phone = record?.org?.phone;
  const formattedPhone = phone ? `+${phone.phoneCc || '1'} (${phone.phoneNdc || ''}) ${phone.phoneLocal || ''}` : '';
  const fax = record?.org?.fax;
  const formattedFax = fax ? `+${fax.phoneCc || '1'} (${fax.phoneNdc || ''}) ${fax.phoneLocal || ''}` : '';

  const schoolDi: IData = {
    org: {
      ...record.org,
      phone: phone ? {
        phoneCc: phone.phoneCc,
        phoneNdc: phone.phoneNdc,
        phoneLocal: phone.phoneLocal,
        formatted: formattedPhone
      } : {} as IPhone,
      fax: fax ? {
        phoneCc: fax.phoneCc,
        phoneNdc: fax.phoneNdc,
        phoneLocal: fax.phoneLocal,
        formatted: formattedFax
      } : {} as IPhone
    },
    di: {
      ...record.di,
      diCode: dischool.diCode
    }
  };
  return schoolDi;
};

const useOpenDI = (dischool: TDISchool) => {
  const [diUpdate, setSchoolDI] = useState<Partial<IDIRequest<IData>> | null>(null);
  const [isLoadingDI, setLoadingDI] = useState<boolean>(false);
  const [errorLoadingDI, setErrorDI] = useState<string>('');

  const retrieve = async (dischool: TDISchool) => {
    setLoadingDI(true);
    try {
      const schoolDi = await mapDI(dischool);
      setSchoolDI({ data: schoolDi });

    } catch (error: any) {
      setErrorDI(error?.message || error);
    }
    setLoadingDI(false);
  };
  useEffect(() => {
    retrieve(dischool);
  }, [dischool]);

  return { diUpdate, setSchoolDI, isLoadingDI, errorLoadingDI };
};

export const DIUpdateFormPage: FC = () => {
  const { isAdmin } = useAuth();
  const location = useLocation();

  const state = location.state as any;
  const summary = state?.summary as ISummary;
  const source = state?.source as string;
  const searchParams = state?.search;

  const file = state?.file as string;
  const diSchool = state?.data as TDISchool; // NEW Update Request

  const readOnly = isAdmin || isReadOnly(summary);
  const filePath = file || (readOnly ? (summary?.submission || summary?.draft) : summary?.draft);

  const { data, isLoading, errorLoading } = useFile(filePath);
  const { diUpdate, isLoadingDI, errorLoadingDI } = useOpenDI(diSchool);

  if (isLoading || isLoadingDI) {
    return <Loader />;
  } else if (errorLoading && errorLoadingDI) {
    return <ErrorNotice errorMessage={errorLoading || errorLoadingDI} />;
  } else if (data) {
    return <DIUpdateRequestForm
      readOnly={readOnly}
      summary={summary}
      request={data}
      source={source}
      searchParams={searchParams}
    />;
  } else if (diUpdate) {
    return <DIUpdateRequestForm
      readOnly={readOnly}
      summary={summary}
      request={diUpdate}
      source={source}
      searchParams={searchParams}
    />;
  } else {
    return <ErrorNotice errorMessage='No data returned from the server, something went wrong!' />;
  }
};